<template>
  <div class="comparison">
    <p v-if="series.length === 0" class="comparison-empty">
      Für den Spieltagsvergleich liegen noch keine Ligaspiele mit Spieltagsnummer vor.
    </p>

    <template v-else>
      <div class="comparison-chart">
        <apexchart ref="chart" type="line" :options="chartOptions" :series="chartSeries" :height="380"
                   @mounted="onChartMounted"></apexchart>

        <p class="comparison-hint">
          Klick auf eine Jahreszahl blendet die Saison aus oder wieder ein.
        </p>
      </div>

      <p class="comparison-note">
        Verglichen wird die Stimmung am jeweiligen Spieltag. Um die Saisons miteinander vergleichbar zu machen, bleiben Pokalspiele außen vor.
      </p>
    </template>

    <!-- Bewusst ausserhalb des v-else oben: die Tabelle zeigt die komplette Saison-Historie, nicht
         nur das MAX_COMPARISON_SEASONS-Fenster des Charts - sie kann also auch dann Zeilen haben,
         wenn die dort sichtbaren Saisons (noch) keine Ligaspieltage haben. -->
    <SeasonStatsTable :seasons="seasons" :highlighted-season="highlightedSeason" />
  </div>
</template>

<script>
import { buildComparisonSeries, defaultComparisonSeason, formatScore, opponentLabel } from './moodHistory.js';
import SeasonStatsTable from './SeasonStatsTable.vue';

/**
 * Saisonvergleich auf einer gemeinsamen Spieltagsachse.
 *
 * Ueber der Kalenderachse liessen sich Saisons nicht uebereinanderlegen; ueber Spieltag 1..34 schon.
 * Farblich ist das Emphasis, nicht Kategorial: die gewaehlte Saison traegt die Akzentfarbe, alle
 * anderen liegen in abgestuftem Grau dahinter. Die Graustufe haengt an der Saison selbst (Position
 * in der Gesamtliste), nicht an ihrem Rang in der Auswahl - eine Saison behaelt ihre Farbe.
 */
export default {
  name: 'SeasonComparisonChart',
  components: { SeasonStatsTable },
  props: {
    seasons: { type: Array, required: true },
    highlightedSeason: { type: String, default: null }
  },
  computed: {
    series() {
      return buildComparisonSeries(this.seasons, this.highlightedSeason);
    },
    chartSeries() {
      return this.series.map(s => ({ name: s.name, data: s.data }));
    },
    /** Vergleichssaison, die zusammen mit der hervorgehobenen standardmaessig sichtbar ist - siehe
     *  defaultComparisonSeason() in moodHistory.js fuer die genaue Regel. */
    defaultComparisonSeasonName() {
      return defaultComparisonSeason(this.seasons, this.highlightedSeason);
    },
    /** Volle Saisonnamen aller Serien, die im Chart standardmaessig eingeblendet sein sollen -
     *  alle anderen im Fenster von buildComparisonSeries starten ausgeblendet, bleiben aber ueber
     *  die Legende zuschaltbar. */
    defaultVisibleSeasons() {
      return [this.highlightedSeason, this.defaultComparisonSeasonName].filter(Boolean);
    },
    /**
     * Letzter Spieltag, an dem die hervorgehobene Saison tatsaechlich gespielt hat.
     *
     * Die Serien liegen auf einer gemeinsamen Spieltagsachse, die spaeteren Punkte einer noch
     * laufenden Saison sind also aufgefuellte Luecken ohne Wert - gesucht ist der letzte davor.
     */
    lastPlayedMatchday() {
      const highlighted = this.series.find(s => s.isHighlighted);
      if (!highlighted) return null;

      const played = highlighted.data.filter(point => point.y !== null);
      return played.length > 0 ? played[played.length - 1].x : null;
    },
    /** Senkrechte Marke am zuletzt gespielten Spieltag der hervorgehobenen Saison. */
    xAxisAnnotations() {
      const matchday = this.lastPlayedMatchday;
      if (matchday === null) return [];

      // Am Saisonende sitzt die Marke ganz rechts; ein mittig ausgerichtetes Label ragte dort
      // aus der Zeichenflaeche und wurde abgeschnitten - deshalb nach links ausrichten.
      const nearRightEdge = matchday >= this.maxMatchday - 3;

      return [{
        x: matchday,
        borderColor: 'var(--chart-ink-mute)',
        strokeDashArray: 4,
        label: {
          text: `${matchday}. Spieltag`,
          orientation: 'horizontal',
          position: 'top',
          offsetY: -4,
          offsetX: nearRightEdge ? -6 : 0,
          textAnchor: nearRightEdge ? 'end' : 'middle',
          borderColor: 'transparent',
          style: { background: 'var(--chart-grid)', color: 'var(--text-primary)', fontSize: '11px' }
        }
      }];
    },
    /** Groesster Spieltag ueber alle sichtbaren Saisons - Bezugspunkt fuer die Randerkennung. */
    maxMatchday() {
      return this.series.reduce((max, s) =>
        s.data.reduce((inner, point) => Math.max(inner, point.x), max), 0);
    },
    /**
     * Achsen-Grundgeruest. ApexCharts ERSETZT in responsive[].options ein angegebenes xaxis/yaxis
     * komplett, statt es zu mergen - eine Teilangabe dort wuerde Formatter und Grenzen mitreissen
     * (Y-Achse stand dann auf "10.00" statt "10"). Deshalb beide Achsen einmal definieren und in
     * den Breakpoints vollstaendig mit nur geaenderten Feldern neu aufbauen.
     */
    axisBase() {
      return {
        x: {
          type: 'numeric',
          tickAmount: 8,
          decimalsInFloat: 0,
          axisBorder: { color: 'var(--chart-grid)' },
          axisTicks: { color: 'var(--chart-grid)' },
          labels: {
            style: { colors: 'var(--chart-ink-mute)', fontSize: '12px' },
            formatter: value => Math.round(value)
          },
          crosshairs: { show: true, stroke: { color: 'var(--chart-ink-mute)', width: 1, dashArray: 0 } },
          tooltip: { enabled: false }
        },
        y: {
          min: 0,
          max: 10,
          tickAmount: 5,
          labels: {
            style: { colors: 'var(--chart-ink-mute)', fontSize: '12px' },
            // Der Formatter beschriftet nicht nur die Achse - ApexCharts schickt auch jeden
            // Serienwert hindurch, und zwar unabhaengig vom Custom-Tooltip: drawSeriesTexts ruft
            // getValuesToPrint in jedem Fall auf. Die aufgefuellten Luecken der gemeinsamen
            // Spieltagsachse kommen hier also als null an und muessen abgefangen werden, sonst
            // bricht der Tooltip beim Ueberfahren mit einem Fehler ab.
            formatter: value => (Number.isFinite(value) ? value.toFixed(0) : '')
          }
        }
      };
    },
    chartOptions() {
      const axis = this.axisBase;
      const titleStyle = { color: 'var(--chart-ink-mute)', fontSize: '12px', fontWeight: 500 };

      return {
        chart: {
          type: 'line',
          background: 'transparent',
          zoom: { enabled: false },
          toolbar: { show: false },
          fontFamily: 'var(--font-family)',
          animations: { enabled: false }
        },
        theme: { mode: 'dark' },
        colors: this.series.map(s => s.color),
        stroke: {
          width: this.series.map(s => (s.isHighlighted ? 3.5 : 1.75)),
          // Strichmuster als zweite Unterscheidung neben der Helligkeit - siehe contextStyle().
          dashArray: this.series.map(s => s.dash),
          curve: 'smooth'
        },
        markers: {
          // Keine dauerhaften Punkte, auch nicht auf der hervorgehobenen Linie.
          //
          // Traegt eine Serie Marker, fuehrt ApexCharts den Crosshair ueber die Marker-Elemente im
          // DOM: enlargePoints() sucht ein .apexcharts-marker, dessen rel dem Datenpunktindex
          // entspricht, und setzt die senkrechte Linie auf dessen Position. An den aufgefuellten
          // Luecken der gemeinsamen Spieltagsachse gibt es kein solches Element - ein Punkt ohne
          // Wert bekommt in plotChartMarkers die Klasse apexcharts-nullpoint, die
          // apexcharts-marker ersetzt statt sie zu ergaenzen. Der Crosshair blieb an genau diesen
          // Spieltagen stehen, waehrend der Tooltip normal erschien.
          //
          // Ohne Marker nimmt ApexCharts den anderen Weg (moveDynamicPointsOnHover ueber
          // pointsArray), und das ist auch an Luecken lueckenlos gefuellt. Die hervorgehobene
          // Saison bleibt durch ihre Farbe und die doppelte Strichstaerke kenntlich, beim
          // Ueberfahren erscheint ihr Punkt weiterhin ueber hover.size.
          size: 0,
          strokeColors: 'var(--chart-surface)',
          strokeWidth: 2,
          // Sonst setzt ApexCharts an jede Luecke einen kleinen Nullpunkt-Marker.
          showNullDataPoints: false,
          hover: { size: 6 }
        },
        dataLabels: { enabled: false },
        annotations: { xaxis: this.xAxisAnnotations },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'center',
          fontSize: '13px',
          labels: { colors: 'var(--chart-ink-mute)' },
          markers: {
            // Der Kasten wird bewusst doppelt vermasst: Aeltere ApexCharts-Fassungen zeichnen den
            // Legendenmarker als CSS-Kaestchen und lesen width/height/radius, neuere zeichnen ihn
            // als SVG und lesen size/strokeWidth. Welche im Browser laeuft, entscheidet das
            // Bundle - beide Angaben nebeneinander halten den Marker in jedem Fall flach.
            width: 20,
            height: 4,
            radius: 0,
            size: 10,
            strokeWidth: 0,
            // Der eigentliche Strich kommt aus customHTML; der Kasten selbst muss farblos sein.
            // Die aeltere Fassung faerbt ihn mit einem !important-Inlinestil in der Serienfarbe -
            // dagegen kommt kein Stylesheet an, weil Inline plus !important die hoechste Stufe der
            // Kaskade ist. fillColors ist der einzige Hebel, der dort noch greift.
            fillColors: this.series.map(() => 'transparent'),
            // Je Serie eine eigene Funktion; ApexCharts ruft customHTML[i]() auf.
            customHTML: this.series.map(s => () => this.legendMarker(s))
          },
          itemMargin: { horizontal: 10, vertical: 2 }
        },
        grid: {
          borderColor: 'var(--chart-grid)',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          padding: { top: 0, right: 16, bottom: 0, left: 8 }
        },
        xaxis: { ...axis.x, title: { text: 'Spieltag', style: titleStyle } },
        yaxis: { ...axis.y, title: { text: 'Stimmung', style: titleStyle } },
        tooltip: {
          theme: 'dark',
          shared: true,
          intersect: false,
          custom: ({ dataPointIndex, w }) => {
            // Alle Serien liegen auf derselben Spieltagsachse (siehe buildComparisonSeries), der
            // Index trifft in jeder Serie denselben Spieltag. Gelesen wird er trotzdem aus den
            // Chartdaten und nicht aus this.series, damit ein Umsortieren durch ApexCharts - etwa
            // beim Aus- und Einblenden ueber die Legende - nicht unbemerkt danebengreift.
            const matchday = (w.globals.seriesX[0] || [])[dataPointIndex];
            if (matchday === undefined || matchday === null) return '';

            const rows = this.series.map(s => {
              const point = s.data.find(p => p.x === matchday);
              // Spieltage, die diese Saison nicht gespielt hat, sind aufgefuellte Luecken.
              if (!point || point.y === null) return '';
              return `<div class="cmp-tooltip-row${s.isHighlighted ? ' cmp-tooltip-row-strong' : ''}">
                  <span class="cmp-tooltip-dot" style="background:${s.color}"></span>
                  <span class="cmp-tooltip-name">${s.name}</span>
                  <span class="cmp-tooltip-opp">${point.evnt ? opponentLabel(point.evnt) : ''}</span>
                  <span class="cmp-tooltip-value">${formatScore(point.y)}</span>
                </div>`;
            }).join('');

            return `<div class="cmp-tooltip">
                <div class="cmp-tooltip-head">${Math.round(matchday)}. Spieltag</div>${rows}
              </div>`;
          }
        },
        responsive: [
          {
            // Auf schmalen Geraeten kosten die Achsentitel Breite, die die Kurve besser braucht.
            breakpoint: 576,
            options: {
              chart: { height: 280 },
              xaxis: { ...axis.x, tickAmount: 5 },
              yaxis: { ...axis.y },
              legend: { fontSize: '12px', itemMargin: { horizontal: 6 } }
            }
          },
          { breakpoint: 992, options: { chart: { height: 320 } } }
        ]
      };
    }
  },
  watch: {
    // Saisonwechsel setzt die Sichtbarkeit auf den neuen Default zurueck - ein manuelles
    // Zu-/Abschalten ueber die Legende gilt nur fuer die gerade betrachtete Saison.
    highlightedSeason() {
      this.$nextTick(this.applyDefaultVisibility);
    }
  },
  methods: {
    /** Feuert, sobald ApexCharts tatsaechlich gerendert hat (nicht zu verwechseln mit Vues
     *  eigenem mounted()-Hook, der frueher laeuft, bevor die Chart-Instanz existiert). */
    onChartMounted() {
      this.applyDefaultVisibility();
    },
    /** Blendet alle Serien bis auf die hervorgehobene und ihre Default-Vergleichssaison aus -
     *  ueber ApexCharts' hideSeries()/showSeries(), denselben Mechanismus, den auch ein Klick auf
     *  die Legende ausloest, die Saisons bleiben also normal zuschaltbar. */
    applyDefaultVisibility() {
      const chart = this.$refs.chart;
      if (!chart) return;

      this.series.forEach(s => {
        if (this.defaultVisibleSeasons.includes(s.seasonName)) chart.showSeries(s.name);
        else chart.hideSeries(s.name);
      });
    },
    /**
     * Markersymbol eines Legendeneintrags: derselbe Strich wie die Linie im Chart, mit demselben
     * Muster und derselben Staerke.
     *
     * Ohne das sehen alle Kontextsaisons in der Legende gleich aus - ApexCharts zeichnet dort
     * unabhaengig von stroke.dashArray immer einen durchgezogenen Balken. Gerade das Muster
     * unterscheidet die Saisons im Chart aber mit, weil drei Grautoene allein bei sich kreuzenden
     * Linien nicht reichen (siehe contextStyle in moodHistory.js); in der Legende war diese
     * Unterscheidung bisher nicht wiederzufinden.
     *
     * Die Farbe kommt ueber currentColor: Bei einem customHTML-Marker setzt ApexCharts die
     * Serienfarbe als color auf den umschliessenden span und macht dessen Hintergrund
     * transparent. So faerbt sich der Strich beim Aus- und Einblenden ueber die Legende mit um.
     */
    legendMarker(series) {
      const height = series.isHighlighted ? 3.5 : 2.5;
      const color = series.color;

      // Das Muster laeuft im halben Massstab.
      //
      // Die groebste Strichlaenge im Chart betraegt 10px. In einem 20px breiten Kaestchen ist
      // davon genau ein Strich zu sehen, gefolgt von einer Luecke, die am Rand endet - das liest
      // sich als durchgezogene Linie und war von der hervorgehobenen Saison nicht zu
      // unterscheiden. Halbiert passen von jedem Muster mindestens zwei Striche hinein, und die
      // Abstufung von fein nach grob bleibt erhalten. Der Marker ist ein Symbol, kein Massstab.
      const dash = series.dash / 2;

      // Ein Verlauf bildet stroke-dasharray genau nach: dash Pixel Farbe, dash Pixel Luecke.
      // Bewusst als HTML-Kasten und nicht als eingebettetes SVG - die Legende haengt in einem
      // foreignObject im Chart-SVG, und ein per innerHTML dort eingesetztes <svg> ist von der
      // Namensraumbehandlung des Parsers abhaengig. Ein span mit Hintergrund ist das nicht.
      //
      // Die Farbe steht ausgeschrieben statt als currentColor: Der Kasten wird ueber
      // markers.fillColors auf transparent gesetzt, und aeltere ApexCharts-Fassungen leiten
      // daraus auch seine Textfarbe ab - currentColor waere dort ebenfalls transparent.
      const background = dash
        ? `repeating-linear-gradient(to right, ${color} 0, ${color} ${dash}px,` +
          ` transparent ${dash}px, transparent ${dash * 2}px)`
        : color;

      return `<span style="display:block;width:20px;height:${height}px;background:${background}"></span>`;
    }
  }
};
</script>

<style scoped>
/* Absicherung gegen den runden Marker-Kasten aelterer ApexCharts-Fassungen. Den Radius setzen sie
   als Inlinewert ohne !important, dagegen kommt ein Stylesheet an - anders als beim Hintergrund,
   der deshalb ueber markers.fillColors neutralisiert wird. */
.comparison :deep(.apexcharts-legend-marker) {
  border-radius: 0 !important;
}

.comparison-chart {
  background: var(--chart-surface);
  border-radius: var(--radius-md);
  padding: var(--spacing-xs);
}

.comparison-empty {
  margin: 0;
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--text-secondary);
  background: var(--chart-surface);
  border-radius: var(--radius-md);
}

/* Sitzt im Chart-Kasten, auf dessen Grund - wie .trend-hint beim Verlauf, und mit denselben
   Werten. Der Hinweis erklaert die Bedienung und gehoert damit ans Chart; .comparison-note
   ordnet dagegen den Inhalt ein und steht ausserhalb. */
.comparison-hint {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-sm);
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.comparison-note {
  margin: var(--spacing-xs) 0 0;
  color: var(--text-secondary);
  font-size: var(--fs-sm);
  line-height: 1.5;
}
</style>

<style>
/* Nicht scoped - ApexCharts haengt den Custom-Tooltip ausserhalb des Komponenten-Scopes ein. */
.cmp-tooltip {
  padding: 10px 12px;
  font-family: var(--font-family);
  min-width: 200px;
}

.cmp-tooltip-head {
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
  margin-bottom: 6px;
}

.cmp-tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-sm);
  line-height: 1.7;
}

.cmp-tooltip-row-strong {
  font-weight: 700;
}

.cmp-tooltip-dot {
  width: 10px;
  height: 3px;
  border-radius: 2px;
  flex-shrink: 0;
}

.cmp-tooltip-name {
  font-variant-numeric: tabular-nums;
}

.cmp-tooltip-opp {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.cmp-tooltip-value {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
</style>
